import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add,
  DeleteOutline,
  RestartAlt,
  ViewColumn
} from '@mui/icons-material';
import {
  createDefaultSectionConfigs,
  getVisibleColumns,
  sortRows
} from './reportPreviewUtils';
import ColumnManagerDialog from './ColumnManagerDialog';
import SectorUsageLineChart from './SectorUsageLineChart';
import { isSectorUsageSection, openLinkedSector } from './sectorUsageChartUtils';
import {
  emptyRowFromColumns,
  getEffectiveSectionRows,
  sectionInstanceKey
} from './esrSectionOverrides';
import { buildEsrHoursLandingsBarChartSvg } from './esrChartSvg';

function formatReportDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function EditableField({
  label,
  value,
  onChange,
  multiline = false,
  minRows = 3,
  fullWidth = true
}) {
  return (
    <TextField
      label={label}
      size="small"
      fullWidth={fullWidth}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      sx={{ mt: 1 }}
    />
  );
}

function InteractiveStaticSection({ section, allowManualEdits = false, override = {}, onOverrideChange }) {
  const title = section.title || section.label;
  const patchField = (key, value) => {
    onOverrideChange?.({ ...override, [key]: value });
  };

  if (section.staticKind === 'prepared-by') {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff' }}>
        <Typography variant="h6" color="primary" gutterBottom>{title}</Typography>
        <Stack spacing={0.5}>
          {section.preparedByName && (
            <Typography variant="body2">Prepared by: {section.preparedByName}</Typography>
          )}
          {section.reportDate && (
            <Typography variant="body2">Report date: {formatReportDate(section.reportDate)}</Typography>
          )}
          {section.reportName && (
            <Typography variant="body2">Report name: {section.reportName}</Typography>
          )}
          {section.scopedAircraft && (
            <Typography variant="body2">Aircraft: {section.scopedAircraft}</Typography>
          )}
        </Stack>
      </Paper>
    );
  }

  if (section.staticKind === 'aircraft-data') {
    const aircraftList = section.aircraftData || [];
    const fieldRows = (aircraft) => {
      const manufacture = formatReportDate(aircraft.dateOfManufacture)
        || aircraft.yearOfManufacture
        || '—';
      const usageAsOn = formatReportDate(aircraft.liveUsageAsOnDate)
        ? `${formatReportDate(aircraft.liveUsageAsOnDate)}${aircraft.liveUsageSheetNumber ? ` (Sheet ${aircraft.liveUsageSheetNumber})` : ''}`
        : '—';
      const flightHours = aircraft.flightHours != null && aircraft.flightHours !== ''
        ? `${aircraft.flightHours} ${aircraft.hourType || 'Hours'}`.trim()
        : '—';
      return [
        ['Registration', aircraft.registration || '—'],
        ['Manufacturer & Model', aircraft.manufacturerModel || '—'],
        ['Aircraft Type', aircraft.aircraftType || '—'],
        ['Serial Number', aircraft.serialNumber || '—'],
        ['Category', aircraft.category || '—'],
        ['Date of Manufacture', manufacture],
        ['Flight Hours (Live)', flightHours],
        ['Landings (Live)', aircraft.hasLandings ? aircraft.landings : (aircraft.landings || '—')],
        ['Usage As On', usageAsOn],
        ['Basic Empty Weight', aircraft.basicEmptyWeight || '—'],
        ['Max All-Up Weight', aircraft.maxAllUpWeight || '—'],
        ['Max Ramp Weight', aircraft.maxRampWeight || '—'],
        ['Max Landing Weight', aircraft.maxLandingWeight || '—'],
        ['Max Fuel Weight', aircraft.maxFuelWeight || '—'],
        ['Fuel Capacity', aircraft.fuelCapacity || '—'],
        ['Fuel Consumption per Hour', aircraft.fuelConsumptionPerHour || '—'],
        ['Fuel Consumption per Hour (Weight)', aircraft.fuelConsumptionPerHourWeight || '—'],
        ['TSN As On Date', formatReportDate(aircraft.tsnAsOnDate) || '—'],
        ['TSN Period Value', aircraft.tsnPeriodValue
          ? `${aircraft.tsnPeriodValue} ${aircraft.tsnPeriod || ''}`.trim()
          : '—'],
        ['TSN Landings', aircraft.tsnLandings || '—']
      ];
    };

    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff' }}>
        <Typography variant="h6" color="primary" gutterBottom>{title}</Typography>
        {aircraftList.length === 0 ? (
          <Typography color="text.secondary" fontStyle="italic">No aircraft selected for this report.</Typography>
        ) : (
          aircraftList.map((aircraft) => (
            <Box key={aircraft.registration || aircraft.serialNumber} mb={2.5}>
              <Typography variant="subtitle2" gutterBottom sx={{ textTransform: 'uppercase' }}>
                {aircraft.registration || 'Aircraft'}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1.25
                }}
              >
                {fieldRows(aircraft).map(([label, value]) => (
                  <Box key={`${aircraft.registration}-${label}`}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase' }}>
                      {label}
                    </Typography>
                    <Typography variant="body2">{value}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))
        )}
      </Paper>
    );
  }

  if (section.staticKind === 'notes-section') {
    const notes = Object.prototype.hasOwnProperty.call(override, 'notes')
      ? override.notes
      : (section.notes || '');
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff' }}>
        <Typography variant="h6" color="primary" gutterBottom>{title}</Typography>
        {allowManualEdits ? (
          <EditableField
            label="Notes / statement"
            value={notes}
            multiline
            minRows={3}
            onChange={(value) => patchField('notes', value)}
          />
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {notes || '—'}
          </Typography>
        )}
      </Paper>
    );
  }

  if (section.staticKind === 'esr-cover'
    || section.staticKind === 'esr-glossary'
    || section.staticKind === 'esr-part-divider') {
    const heading = Object.prototype.hasOwnProperty.call(override, 'heading')
      ? override.heading
      : (section.heading || title);
    const periodLabel = Object.prototype.hasOwnProperty.call(override, 'periodLabel')
      ? override.periodLabel
      : (section.periodLabel || '');
    const referenceLine = Object.prototype.hasOwnProperty.call(override, 'referenceLine')
      ? override.referenceLine
      : (section.referenceLine || '');
    const operatorName = Object.prototype.hasOwnProperty.call(override, 'operatorName')
      ? override.operatorName
      : (section.operatorName || '');
    const operatorAddress = Object.prototype.hasOwnProperty.call(override, 'operatorAddress')
      ? override.operatorAddress
      : (section.operatorAddress || '');
    const body = Object.prototype.hasOwnProperty.call(override, 'body')
      ? override.body
      : (section.body || '');

    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff' }}>
        <Typography variant="h6" color="primary" gutterBottom>
          {heading || title}
        </Typography>
        {allowManualEdits ? (
          <Stack spacing={1}>
            <EditableField label="Heading" value={heading} onChange={(v) => patchField('heading', v)} />
            {section.staticKind === 'esr-cover' && (
              <>
                <EditableField label="Period" value={periodLabel} onChange={(v) => patchField('periodLabel', v)} />
                <EditableField label="Reference" value={referenceLine} onChange={(v) => patchField('referenceLine', v)} />
                <EditableField label="Operator" value={operatorName} onChange={(v) => patchField('operatorName', v)} />
                <EditableField label="Address" value={operatorAddress} onChange={(v) => patchField('operatorAddress', v)} />
              </>
            )}
            <EditableField
              label={section.staticKind === 'esr-glossary' ? 'Glossary text' : 'Body text'}
              value={body}
              multiline
              minRows={section.staticKind === 'esr-glossary' ? 8 : 3}
              onChange={(v) => patchField('body', v)}
            />
          </Stack>
        ) : (
          <Stack spacing={0.5}>
            {periodLabel && (
              <Typography variant="body2">Period: {periodLabel}</Typography>
            )}
            {referenceLine && (
              <Typography variant="body2">{referenceLine}</Typography>
            )}
            {operatorName && (
              <Typography variant="body2">Operator: {operatorName}</Typography>
            )}
            {operatorAddress && (
              <Typography variant="body2">Address: {operatorAddress}</Typography>
            )}
            {body && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                {body}
              </Typography>
            )}
          </Stack>
        )}
      </Paper>
    );
  }

  if (section.staticKind === 'esr-distribution') {
    const heading = Object.prototype.hasOwnProperty.call(override, 'heading')
      ? override.heading
      : (section.heading || title);
    const lines = Array.isArray(override.lines) ? override.lines : (section.lines || []);
    const body = Object.prototype.hasOwnProperty.call(override, 'body')
      ? override.body
      : (section.body || '');

    const updateLine = (idx, field, value) => {
      const next = lines.map((row, i) => (i === idx ? { ...row, [field]: value } : row));
      patchField('lines', next);
    };

    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff' }}>
        <Typography variant="h6" color="primary" gutterBottom>
          {heading || title}
        </Typography>
        {allowManualEdits ? (
          <Stack spacing={1}>
            <EditableField label="Heading" value={heading} onChange={(v) => patchField('heading', v)} />
            <EditableField
              label="Free-text distribution (optional)"
              value={body}
              multiline
              minRows={2}
              onChange={(v) => patchField('body', v)}
            />
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}>Copy No</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}>Document Holder</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}>Distribution</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5', width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.copyNo ?? ''}
                          onChange={(e) => updateLine(idx, 'copyNo', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.holder ?? ''}
                          onChange={(e) => updateLine(idx, 'holder', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.distribution ?? ''}
                          onChange={(e) => updateLine(idx, 'distribution', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label="Remove row"
                          onClick={() => patchField('lines', lines.filter((_, i) => i !== idx))}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => patchField('lines', [...lines, { copyNo: '', holder: '', distribution: '' }])}
            >
              Add distribution row
            </Button>
          </Stack>
        ) : body ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{body}</Typography>
        ) : lines.length === 0 ? (
          <Typography color="text.secondary" fontStyle="italic">No distribution entries.</Typography>
        ) : (
          <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Copy No</TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Document Holder</TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Distribution</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.copyNo}</TableCell>
                    <TableCell>{row.holder}</TableCell>
                    <TableCell>{row.distribution}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    );
  }

  if (section.staticKind === 'flight-summary' || section.staticKind === 'crew-block') {
    const columns = section.columns || [];
    const rows = section.rows || [];
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff' }}>
        <Typography variant="h6" color="primary" gutterBottom>{title}</Typography>
        {rows.length === 0 ? (
          <Typography color="text.secondary" fontStyle="italic">No records available.</Typography>
        ) : (
          <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.key} sx={{ bgcolor: '#f5f5f5', whiteSpace: 'nowrap' }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columns.map((col) => (
                      <TableCell key={col.key}>{row[col.key] ?? ''}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {section.rowCount ?? rows.length} row(s)
          {section.truncated ? ' — showing first records' : ''}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff' }}>
      <Typography variant="h6" color="primary" gutterBottom>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{section.label || section.sectionKey}</Typography>
    </Paper>
  );
}

function InteractiveSectionTable({
  section,
  config,
  onConfigChange,
  allowManualEdits = false,
  override = {},
  onOverrideChange
}) {
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const displayTitle = config.titleOverride || section.title;
  const editableRows = useMemo(
    () => getEffectiveSectionRows(section, { [sectionInstanceKey(section)]: override }),
    [section, override]
  );
  const chartSvg = useMemo(() => {
    if (section.presentation !== 'chart' && !section.chartSvg && !String(section.sectionKey || '').includes('esr-hours-by-aircraft')) {
      return section.chartSvg || '';
    }
    const title = section.chartTitle || section.title || 'HOURS / LANDINGS LOGGED AIRCRAFT WISE';
    return buildEsrHoursLandingsBarChartSvg(editableRows, { title });
  }, [section, editableRows]);
  const visibleColumns = useMemo(
    () => getVisibleColumns(section, config),
    [section, config]
  );
  const displayRows = useMemo(
    () => (allowManualEdits ? editableRows : sortRows(editableRows, config.sortBy, config.sortOrder)),
    [allowManualEdits, editableRows, config.sortBy, config.sortOrder]
  );

  const setRows = (rows) => {
    onOverrideChange?.({ ...override, rows });
  };

  const updateCell = (rowIndex, key, value) => {
    const next = editableRows.map((row, idx) => (
      idx === rowIndex ? { ...row, [key]: value } : row
    ));
    setRows(next);
  };

  const handleSort = (key) => {
    if (allowManualEdits) return;
    if (config.sortBy === key) {
      if (config.sortOrder === 'asc') {
        onConfigChange({ ...config, sortOrder: 'desc' });
      } else {
        onConfigChange({ ...config, sortBy: null, sortOrder: 'asc' });
      }
      return;
    }
    onConfigChange({ ...config, sortBy: key, sortOrder: 'asc' });
  };

  const resetColumns = () => {
    onConfigChange({
      columnOrder: (section.columns || []).map((col) => col.key),
      hiddenKeys: [],
      titleOverride: '',
      showTitle: true,
      sortBy: null,
      sortOrder: 'asc'
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={1}>
        <Box>
          {config.showTitle !== false && displayTitle && (
            <Typography variant="h6" color="primary">
              {displayTitle}
            </Typography>
          )}
          {config.showTitle !== false && section.label && section.label !== displayTitle && (
            <Typography variant="caption" color="text.secondary">
              {section.label}
            </Typography>
          )}
          {allowManualEdits && (
            <Typography variant="caption" color="text.secondary" display="block">
              Edit cells below — changes are included when you download PDF/CSV
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          {!allowManualEdits && (
            <>
              <Tooltip title="Reset columns and sort">
                <IconButton size="small" onClick={resetColumns}>
                  <RestartAlt fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ViewColumn />}
                onClick={() => setColumnDialogOpen(true)}
              >
                Columns
              </Button>
            </>
          )}
          {allowManualEdits && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setRows([...editableRows, emptyRowFromColumns(section.columns || [])])}
            >
              Add row
            </Button>
          )}
        </Stack>
      </Box>

      {!allowManualEdits && isSectorUsageSection(section.sectionKey) && (
        <SectorUsageLineChart section={section} height={300} />
      )}

      {(chartSvg || section.chartSvg) && (
        <Box sx={{ my: 2, textAlign: 'center', overflowX: 'auto' }}>
          {section.chartTitle && (
            <Typography variant="subtitle2" gutterBottom>{section.chartTitle}</Typography>
          )}
          <Box
            sx={{
              display: 'inline-block',
              maxWidth: '100%',
              '& svg': { maxWidth: '100%', height: 'auto' }
            }}
            dangerouslySetInnerHTML={{ __html: chartSvg || section.chartSvg }}
          />
          {section.chartNote && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {section.chartNote}
            </Typography>
          )}
        </Box>
      )}

      {visibleColumns.length === 0 ? (
        <Typography color="text.secondary">No columns selected. Use Columns to add columns.</Typography>
      ) : displayRows.length === 0 ? (
        <Typography color="text.secondary" fontStyle="italic">
          No records match the selected filters.
          {allowManualEdits ? ' Use Add row to enter data manually.' : ''}
        </Typography>
      ) : (section.hideDataTable && !allowManualEdits) ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {displayRows.length} aircraft data point(s) plotted on the chart above
        </Typography>
      ) : (
        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableCell key={col.key} sx={{ bgcolor: '#f5f5f5', whiteSpace: 'nowrap' }}>
                    {allowManualEdits ? col.label : (
                      <TableSortLabel
                        active={config.sortBy === col.key}
                        direction={config.sortBy === col.key ? config.sortOrder : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                {allowManualEdits && (
                  <TableCell sx={{ bgcolor: '#f5f5f5', width: 48 }} />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row, rowIndex) => {
                const linkedSectorId = row.linkedSectorId ? String(row.linkedSectorId) : '';
                const clickable = !allowManualEdits
                  && Boolean(linkedSectorId)
                  && isSectorUsageSection(section.sectionKey);
                return (
                  <TableRow
                    key={rowIndex}
                    hover
                    onClick={() => {
                      if (clickable) openLinkedSector(linkedSectorId);
                    }}
                    sx={clickable ? { cursor: 'pointer' } : undefined}
                    title={clickable ? 'Open sector details' : undefined}
                  >
                    {visibleColumns.map((col) => (
                      <TableCell key={col.key} sx={{ minWidth: allowManualEdits ? 120 : undefined }}>
                        {allowManualEdits ? (
                          <TextField
                            size="small"
                            fullWidth
                            multiline={String(row[col.key] ?? '').length > 40}
                            minRows={1}
                            value={row[col.key] ?? ''}
                            onChange={(e) => updateCell(rowIndex, col.key, e.target.value)}
                          />
                        ) : (
                          row[col.key] ?? ''
                        )}
                      </TableCell>
                    ))}
                    {allowManualEdits && (
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label="Remove row"
                          onClick={() => setRows(editableRows.filter((_, i) => i !== rowIndex))}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {displayRows.length} row(s)
        {section.truncated && !allowManualEdits ? ' — showing first records' : ''}
        {!allowManualEdits && config.sortBy ? ` · Sorted by ${visibleColumns.find((c) => c.key === config.sortBy)?.label || config.sortBy} (${config.sortOrder})` : ''}
        {!allowManualEdits && isSectorUsageSection(section.sectionKey)
          ? ' · Click a linked sector row or chart point to open the sector'
          : ''}
      </Typography>

      {!allowManualEdits && (
        <ColumnManagerDialog
          open={columnDialogOpen}
          section={section}
          config={config}
          onClose={() => setColumnDialogOpen(false)}
          onApply={(patch) => {
            onConfigChange({
              ...config,
              ...patch,
              sortBy: config.sortBy && patch.hiddenKeys?.includes(config.sortBy) ? null : config.sortBy
            });
            setColumnDialogOpen(false);
          }}
        />
      )}
    </Paper>
  );
}

/**
 * Interactive report preview with per-section column reorder, hide, and sort.
 * When allowManualEdits is true (ESR), static text and table cells are editable.
 */
const ReportInteractivePreview = ({
  reportData,
  onSectionConfigsChange,
  heading,
  showHeading = true,
  initialSectionConfigs,
  allowManualEdits = false,
  sectionOverrides = {},
  onSectionOverridesChange
}) => {
  const [sectionConfigs, setSectionConfigs] = useState({});

  useEffect(() => {
    if (!reportData?.sections) {
      setSectionConfigs({});
      return;
    }
    const defaults = createDefaultSectionConfigs(reportData);
    setSectionConfigs(initialSectionConfigs
      ? { ...defaults, ...initialSectionConfigs }
      : defaults);
  }, [reportData, initialSectionConfigs]);

  useEffect(() => {
    onSectionConfigsChange?.(sectionConfigs);
  }, [sectionConfigs, onSectionConfigsChange]);

  const updateSectionConfig = (instanceKey, nextConfig) => {
    setSectionConfigs((prev) => ({
      ...prev,
      [instanceKey]: nextConfig
    }));
  };

  const updateSectionOverride = (instanceKey, nextOverride) => {
    onSectionOverridesChange?.({
      ...sectionOverrides,
      [instanceKey]: nextOverride
    });
  };

  if (!reportData) {
    return null;
  }

  const reportDateLabel = formatReportDate(reportData.reportDate);

  return (
    <Box sx={{ maxWidth: '297mm', mx: 'auto', bgcolor: '#fff', p: { xs: 2, md: 3 }, boxShadow: 1 }}>
      {showHeading && (
        <Typography variant="h5" gutterBottom>
          {heading || reportData.reportName || 'Report'}
        </Typography>
      )}
      {allowManualEdits && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Manual edit mode: update text and table values here. Downloads use your edits.
        </Typography>
      )}
      <Stack spacing={0.5} mb={2}>
        {reportData.operatorName && (
          <Typography variant="body2" color="text.secondary">
            Operator: {reportData.operatorName}
          </Typography>
        )}
        {reportData.preparedByName && (
          <Typography variant="body2" color="text.secondary">
            Prepared by: {reportData.preparedByName}
          </Typography>
        )}
        {reportDateLabel && (
          <Typography variant="body2" color="text.secondary">
            Report date: {reportDateLabel}
          </Typography>
        )}
        {reportData.scopedAircraft && (
          <Typography variant="body2" color="text.secondary">
            Aircraft: {reportData.scopedAircraft}
          </Typography>
        )}
      </Stack>
      <Divider sx={{ mb: 2 }} />

      {(reportData.sections || []).length === 0 ? (
        <Typography color="text.secondary">No sections in this report.</Typography>
      ) : (
        reportData.sections.map((section) => {
          const instanceKey = sectionInstanceKey(section);
          if (section.sectionType === 'static') {
            return (
              <InteractiveStaticSection
                key={instanceKey}
                section={section}
                allowManualEdits={allowManualEdits}
                override={sectionOverrides[instanceKey] || {}}
                onOverrideChange={(next) => updateSectionOverride(instanceKey, next)}
              />
            );
          }
          return (
            <InteractiveSectionTable
              key={instanceKey}
              section={section}
              config={sectionConfigs[instanceKey] || createDefaultSectionConfigs({ sections: [section] })[instanceKey]}
              onConfigChange={(next) => updateSectionConfig(instanceKey, next)}
              allowManualEdits={allowManualEdits}
              override={sectionOverrides[instanceKey] || {}}
              onOverrideChange={(next) => updateSectionOverride(instanceKey, next)}
            />
          );
        })
      )}
    </Box>
  );
};

export default ReportInteractivePreview;
